import {Select} from '@wandb/weave/components/Form/Select';
import _ from 'lodash';
import React, {useMemo, useState} from 'react';

import {isCustomWeaveTypePayload} from '../../typeViews/customWeaveType.types';
import {
  CustomWeaveTypeDispatcher,
  CustomWeaveTypeProjectContext,
} from '../../typeViews/CustomWeaveTypeDispatcher';
import {CallSchema} from '../wfReactInterface/wfDataModelHooksInterface';

export type CallViewEntry = {name: string; data: any};

export const CallViews: React.FC<{
  call: CallSchema;
  views: CallViewEntry[];
}> = ({call, views}) => {
  const sortedViews: CallViewEntry[] = useMemo(
    () => _.sortBy(views, v => v.name?.toString() ?? ''),
    [views]
  );

  const [selectedViewName, setSelectedViewName] = useState<string>(
    () => sortedViews[0]?.name ?? ''
  );

  const normalizedSelectedViewName = useMemo(() => {
    if (sortedViews.length === 0) {
      return '';
    }
    const match = sortedViews.find(v => v.name === selectedViewName);
    return match?.name ?? sortedViews[0]?.name ?? '';
  }, [selectedViewName, sortedViews]);

  const selectedView = useMemo(
    () =>
      sortedViews.find(v => v.name === normalizedSelectedViewName) ??
      sortedViews[0] ??
      null,
    [sortedViews, normalizedSelectedViewName]
  );

  const selectOptions = useMemo(
    () => sortedViews.map(v => ({value: v.name, label: v.name})),
    [sortedViews]
  );
  const selectedOption = useMemo(
    () =>
      selectOptions.find(opt => opt.value === normalizedSelectedViewName) ??
      null,
    [selectOptions, normalizedSelectedViewName]
  );

  const hasViews = sortedViews.length > 0;
  const hasMultipleViews = sortedViews.length > 1;
  const isCustomPayload =
    selectedView != null && isCustomWeaveTypePayload(selectedView.data);
  const isContentView =
    isCustomPayload &&
    selectedView.data.weave_type?.type ===
      'weave.type_wrappers.Content.content.Content';

  if (!hasViews) {
    return <div className="px-16 py-12">No custom views logged.</div>;
  }

  return (
    <div className="flex h-full flex-col overflow-hidden px-16 pt-12">
      <CustomWeaveTypeProjectContext.Provider
        value={{entity: call.entity, project: call.project}}>
        <div className="flex h-full flex-col gap-8">
          <div className="flex items-start justify-between gap-8 border-b border-moon-200 pb-8">
            {!hasMultipleViews ? (
              <div className="flex flex-col gap-1">
                <div className="text-lg font-semibold text-moon-900">
                  {selectedView?.name ?? ''}
                </div>
              </div>
            ) : (
              <div className="min-w-[160px]">
                <Select
                  size="medium"
                  value={selectedOption as any}
                  options={selectOptions as any}
                  isClearable={false}
                  onChange={(opt: any) =>
                    setSelectedViewName(
                      opt?.value ?? sortedViews[0]?.name ?? ''
                    )
                  }
                />
              </div>
            )}
          </div>

          {selectedView && (
            <div className="flex-1 min-h-0">
              {isCustomPayload ? (
                <div className={isContentView ? 'h-full min-h-0' : undefined}>
                  <CustomWeaveTypeDispatcher data={selectedView.data} />
                </div>
              ) : (
                <pre className="h-full overflow-auto text-[12px] leading-[16px]">
                  {typeof selectedView.data === 'object'
                    ? JSON.stringify(selectedView.data, null, 2)
                    : String(selectedView.data)}
                </pre>
              )}
            </div>
          )}
        </div>
      </CustomWeaveTypeProjectContext.Provider>
    </div>
  );
};
